import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {firstValueFrom, map, Observable, of} from 'rxjs';
import {createUpdatePlanCommand} from 'src/app/domain/command/plan/createUpdatePlanCommand';
import {UpdatePlan} from 'src/app/domain/command/plan/plan.command';
import {
  BackofficePortfolio,
  filterPortfoliosNotAtMaxContracts,
  findRecommendedPortfolio
} from 'src/app/domain/model/backoffice-portfolio/backoffice-portfolio.model';
import {isPhysicalCustomer} from 'src/app/domain/model/customer/customer.model';
import {CustomerPortfolio} from 'src/app/domain/model/customer-portfolio.model';
import {Plan, startPlan} from 'src/app/domain/model/plan/plan.model';
import {getProductType, ProductId} from 'src/app/domain/model/product/product.model';
import BackofficePortfolioApi from 'src/app/domain/service/api/backoffice-portfolio.api';
import CustomerApi from 'src/app/domain/service/api/customer/customer.api';
import CustomerPortfolioApi from 'src/app/domain/service/api/customer-portfolio.api';
import PlanApi from 'src/app/domain/service/api/plan.api';
import PlanRouteResolver from 'src/app/domain/service/route-resolver/plan-route-resolver.service';
import {AppEnvironmentService} from 'src/app/infrastructure/environment/app-environment/app-environment.service';
import {ContractProductService} from 'src/app/infrastructure/service/contracting/product/contract-product.service';
import {TrackService} from 'src/app/infrastructure/service/track.service';
import {
  CustomerAreaPortfolioService
} from 'src/app/infrastructure/service/contracting/shared/portfolio/customer-area-portfolio.service';

@Injectable({
  providedIn: 'root'
})
export class CustomerAreaContractProductService implements ContractProductService {

  constructor(
    private readonly customerApi: CustomerApi,
    private readonly customerPortfolioService: CustomerAreaPortfolioService,
    private readonly planApi: PlanApi,
    private readonly backofficePortfolioApi: BackofficePortfolioApi,
    private readonly customerPortfolioApi: CustomerPortfolioApi,
    private readonly environment: AppEnvironmentService,
    private readonly routeResolver: PlanRouteResolver,
    private readonly trackService: TrackService,
    private readonly router: Router
  ) {}

  public async selectProductAndNavigate(product: ProductId, child: boolean, replaceUrl: boolean): Promise<void> {
    const plan = await this.applyProductSelection(product, child);
    await this.router.navigate(
      [this.routeResolver.resolve(this.router, plan)],
      {replaceUrl}
    );
  }

  public async selectProductAndStartCreation(product: ProductId, child: boolean): Promise<void> {
    await this.applyProductSelection(product, child);
    await this.router.navigate([this.routeResolver.resolveSelectInvestmentStrategyUrl()]);
  }

  public selectProduct(product: ProductId, child: boolean = false): void {
    (async () => {
      const {plan, command} = await this.buildProductSelectionContext(product, child);
      this.planApi.update(command);
      this.trackService.trackProductSelected(getProductType(plan.planSettings.product));
    })();
  }

  public canManageInvestmentPlans(): Observable<boolean> {
    return of(this.environment.canManageInvestmentPlans());
  }

  public canManagePensionPlans(): Observable<boolean> {
    return this.customerApi.customer().pipe(
      map((customer) => this.environment.canManagePensionPlans() && isPhysicalCustomer(customer))
    );
  }

  public canManageUnitLinkedPlans(): Observable<boolean> {
    return this.customerApi.customer().pipe(
      map((customer) => this.environment.canManageUnitLinkedPlans() && isPhysicalCustomer(customer))
    );
  }

  public canTruncateStrategyNames(): boolean {
    return true;
  }

  public getTolerance(): Promise<number> {
    return this.customerApi.waitFetchAndSync().then(customer => customer.tolerance);
  }

  private async applyProductSelection(product: ProductId, child: boolean): Promise<Plan> {
    const {plan, command} = await this.buildProductSelectionContext(product, child);
    await this.planApi.update(command);
    this.trackService.trackProductSelected(getProductType(plan.planSettings.product));

    return plan;
  }

  private async buildProductSelectionContext(product: ProductId, child: boolean): Promise<{plan: Plan; command: UpdatePlan}> {
    const planPromise = this.planApi.waitFetchAndSync();
    const customerPromise = this.customerApi.waitFetchAndSync();
    const boPortfoliosPromise = this.backofficePortfolioApi.waitFetchAllAndSync();

    const plan = await planPromise;
    const customer = await customerPromise;
    const boPortfolios = await boPortfoliosPromise;

    await this.customerPortfolioApi.refresh();
    const customerPortfolios = this.customerPortfolioApi.syncAll();
    const portfolioMaster = await this.getPortfolioMaster(boPortfolios, product, customer.tolerance, customerPortfolios);
    const command = createUpdatePlanCommand(startPlan(customer, plan, product, portfolioMaster, child));

    return {plan, command};
  }

  private async getPortfolioMaster(
    boPortfolios: BackofficePortfolio[],
    productId: ProductId,
    tolerance: number,
    customerPortfolios: CustomerPortfolio[] = []
  ) {
    const currentBoPortfolio = await firstValueFrom(this.customerPortfolioService.currentBoPortfolio$());
    const availablePortfolios = filterPortfoliosNotAtMaxContracts(boPortfolios, customerPortfolios);
    return findRecommendedPortfolio(
      availablePortfolios.length ? availablePortfolios : boPortfolios,
      productId,
      tolerance,
      currentBoPortfolio?.investmentStrategy
    );
  }
}
